import React, { useState } from 'react';
import HistorialService from '../services/historiales';
import { notificationSystem } from '../utils/notifications';

function ObjetivosTerapeuticos({ objetivos, paciente_id, onRefresh }) {
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    objetivo: '',
    descripcion: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_limite: ''
  });

  const [editandoProgreso, setEditandoProgreso] = useState(null);
  const [nuevoProgreso, setNuevoProgreso] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await HistorialService.agregarObjetivo(paciente_id, formData);
      notificationSystem.success('Objetivo terapéutico creado');
      setMostrarFormulario(false);
      setFormData({
        objetivo: '',
        descripcion: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_limite: ''
      });
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      notificationSystem.error('Error al guardar objetivo');
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarProgreso = async (objetivo_id) => {
    try {
      const progreso = Math.min(100, Math.max(0, parseInt(nuevoProgreso[objetivo_id]) || 0));
      await HistorialService.actualizarProgresoObjetivo(paciente_id, objetivo_id, progreso);
      notificationSystem.success('Progreso actualizado');
      setEditandoProgreso(null);
      setNuevoProgreso({});
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      notificationSystem.error('Error al actualizar progreso');
    }
  };

  const getColorProgreso = (progreso) => {
    if (progreso < 33) return '#dc3545';
    if (progreso < 66) return '#ffc107';
    return '#28a745';
  };

  return (
    <div className="objetivos-section">
      <button 
        className="btn-agregar" 
        onClick={() => setMostrarFormulario(!mostrarFormulario)}
      >
        + Agregar Objetivo Terapéutico
      </button>

      {mostrarFormulario && (
        <form onSubmit={handleSubmit} className="formulario">
          <div>
            <label>Objetivo Principal *</label>
            <input 
              type="text" 
              name="objetivo"
              placeholder="Describa el objetivo principal"
              value={formData.objetivo}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label>Descripción Detallada</label>
            <textarea 
              name="descripcion"
              placeholder="Descripción más detallada del objetivo y cómo alcanzarlo"
              value={formData.descripcion}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form-row">
            <div>
              <label>Fecha de Inicio *</label>
              <input 
                type="date" 
                name="fecha_inicio"
                value={formData.fecha_inicio}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label>Fecha Límite</label>
              <input 
                type="date" 
                name="fecha_limite"
                value={formData.fecha_limite}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-buttons">
            <button type="submit" className="btn-guardar" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar Objetivo'}
            </button>
            <button 
              type="button" 
              className="btn-cancelar" 
              onClick={() => setMostrarFormulario(false)}
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="objetivos-lista">
        {objetivos.length === 0 ? (
          <p className="no-data">No hay objetivos terapéuticos registrados</p>
        ) : (
          objetivos.map((obj) => {
            const progreso = obj.progreso_porcentaje || 0;
            const editando = editandoProgreso === obj.id;

            return (
              <div key={obj.id} className="objetivo-card">
                <div className="objetivo-header">
                  <div>
                    <h4>🎯 {obj.objetivo}</h4>
                  </div>
                  {obj.fecha_limite && (
                    <small className="fecha-limite">
                      Límite: {new Date(obj.fecha_limite).toLocaleDateString()}
                    </small>
                  )}
                </div>

                {obj.descripcion && (
                  <p className="objetivo-descripcion">{obj.descripcion}</p>
                )}

                <div className="progreso-section">
                  <div className="progreso-header">
                    <strong>Progreso</strong>
                    {editando ? (
                      <div className="progreso-input">
                        <input 
                          type="number" 
                          min="0" 
                          max="100"
                          value={nuevoProgreso[obj.id] || progreso}
                          onChange={(e) => setNuevoProgreso({...nuevoProgreso, [obj.id]: e.target.value})}
                        />
                        <span>%</span>
                      </div>
                    ) : (
                      <span className="progreso-valor">{progreso}%</span>
                    )}
                  </div>

                  <div className="progreso-bar">
                    <div 
                      className="progreso-fill"
                      style={{
                        width: `${progreso}%`,
                        backgroundColor: getColorProgreso(progreso)
                      }}
                    >
                      {progreso > 10 && <span>{progreso}%</span>}
                    </div>
                  </div>

                  {editando ? (
                    <div className="progreso-botones">
                      <button 
                        className="btn-guardar-pequeño"
                        onClick={() => handleActualizarProgreso(obj.id)}
                      >
                        Guardar
                      </button>
                      <button 
                        className="btn-cancelar-pequeño"
                        onClick={() => {
                          setEditandoProgreso(null);
                          setNuevoProgreso({});
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="btn-editar-progreso"
                      onClick={() => {
                        setEditandoProgreso(obj.id);
                        setNuevoProgreso({[obj.id]: progreso});
                      }}
                    >
                      Actualizar Progreso
                    </button>
                  )}
                </div>

                <div className="objetivo-fechas">
                  <small>
                    📅 Inicio: {new Date(obj.fecha_inicio).toLocaleDateString()}
                  </small>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ObjetivosTerapeuticos;
